/**
 * `/auth/callback?token=…`.
 *
 * Verification runs exactly once per mount: the token is single-use, so a
 * double-invoked effect would burn it and report "already used" on the attempt
 * that mattered.
 */

import { useEffect, useRef, useState } from 'react';
import { auth } from '../lib/api';
import { ErrorNotice, Loading } from '../components/Feedback';
import { useSession } from '../lib/session';
import { Link, navigate, useLocation } from '../router/router';

export function AuthCallback() {
  const { query } = useLocation();
  const { reload } = useSession();
  const [error, setError] = useState<unknown>(null);
  const started = useRef(false);

  const token = query.get('token') ?? '';

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!token) {
      setError(new Error('That link is missing its token.'));
      return;
    }

    void auth
      .verify(token)
      .then(async () => {
        await reload();
        navigate('/', { replace: true });
      })
      .catch(setError);
  }, [token, reload]);

  if (error) {
    return (
      <div className="page">
        <div className="wrap wrap--narrow stack" style={{ paddingTop: 'var(--s-8)' }}>
          <h1>Could not sign you in</h1>
          <ErrorNotice error={error} />
          <Link className="btn" to="/signin">
            Request a new sign-in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <Loading label="Signing you in" />
    </div>
  );
}
